import { apiClient } from '@/services/core/apiClient';

export interface VisiblePatient {
  patientProgramId: number;
  enrollmentPatientId: number;
  currentTaskId: number | null;
  currentWorkflowAttempt: number | null;
  workflowId: number | null;
  userGroupId: number | null;
  assignedUserId: number | null;
  activityId: number | null;
  allowedFromDate: string | null;
  allowedToDate: string | null;
  activityTimezone: string | null;
  dayOfWeekUtc: number | null;
  visibilityFromTimeUtc: string | null;
  visibilityToTimeUtc: string | null;
  updatedDatetime: string | null;
  createdDatetime: string | null;
  viewLockDt: string | null;
  lockedBy: number | null;
  patientFirstName: string | null;
  patientLastName: string | null;
  clinicName: string | null;
  emrId: string | null;
  dob: string | null;
  userGroupName?: string | null;
}

export interface VisiblePatientsApiResponse {
  success: boolean;
  message: string;
  data: VisiblePatient[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  timestamp: number;
  errorType?: string;
}

// Mapped interface for dashboard display compatibility
export interface MappedVisiblePatient {
  patient_id: number;
  patient_program_id: number;
  patient_full_name: string;
  dob: string;
  emr_id: string;
  user_group_name: string;
  clinic_name: string;
  activity_id: number | null;
  workflow_id: number | null;
  assigned_user_id: number | null;
  current_task_id: number | null;
  current_workflow_attempt: number | null;
  user_group_id: number | null;
}

export interface VisiblePatientsResult {
  patients: MappedVisiblePatient[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface VisiblePatientsSearchParams {
  searchBy?: string;
  searchTerm?: string;
}

class VisiblePatientsService {
  /**
   * Fetch visible patients for a user with pagination and optional search
   * @param userId - The hboxuser_id of the logged-in user
   * @param page - Page number (0-based index)
   * @param size - Number of items per page
   * @param token - Authorization token
   * @param searchParams - Optional search parameters
   */
  async getVisiblePatients(
    userId: number,
    page: number = 0,
    size: number = 10,
    token?: string,
    searchParams?: VisiblePatientsSearchParams
  ): Promise<VisiblePatientsResult> {
    try {
      console.log('[VisiblePatientsService] Fetching visible patients:', { userId, page, size, searchParams });
      
      // Build query string with search parameters
      let queryString = `page=${page}&size=${size}`;
      
      if (searchParams?.searchBy && searchParams?.searchTerm) {
        const { searchBy, searchTerm } = searchParams;
        
        // Map frontend search types to API parameters
        switch (searchBy) {
          case 'name':
            queryString += `&patientName=${encodeURIComponent(searchTerm)}`;
            break;
          case 'dob':
            queryString += `&dob=${encodeURIComponent(searchTerm)}`;
            break;
          case 'emr_id':
            queryString += `&emrId=${encodeURIComponent(searchTerm)}`;
            break;
          case 'user_group':
            queryString += `&userGroupName=${encodeURIComponent(searchTerm)}`;
            break;
          case 'clinic':
            queryString += `&clinicName=${encodeURIComponent(searchTerm)}`;
            break;
        }
      }
      
      const response = await apiClient.get<VisiblePatientsApiResponse>(
        `/api/ngpep/${userId}/visiblePatients?${queryString}`,
        token
      );

      console.log('[VisiblePatientsService] API Response:', response);

      // apiClient wraps response - actual API data is in response.data
      const apiData = response.data;

      if (!response.success || !apiData?.success) {
        console.error('[VisiblePatientsService] API returned error:', apiData?.message || response.error);
        return {
          patients: [],
          totalItems: 0,
          totalPages: 0,
          currentPage: page,
          pageSize: size,
          hasNext: false,
          hasPrevious: false
        };
      }

      // Map API response to dashboard-compatible format
      const patientsData = apiData.data || [];
      const mappedPatients: MappedVisiblePatient[] = patientsData.map((patient) => ({
        patient_id: patient.enrollmentPatientId,
        patient_program_id: patient.patientProgramId,
        patient_full_name: [patient.patientFirstName, patient.patientLastName]
          .filter(Boolean)
          .join(' ') || 'N/A',
        dob: patient.dob || '',
        emr_id: patient.emrId || '',
        user_group_name: patient.userGroupName || '',
        clinic_name: patient.clinicName || '',
        activity_id: patient.activityId,
        workflow_id: patient.workflowId,
        assigned_user_id: patient.assignedUserId,
        current_task_id: patient.currentTaskId,
        current_workflow_attempt: patient.currentWorkflowAttempt,
        user_group_id: patient.userGroupId
      }));

      return {
        patients: mappedPatients,
        totalItems: apiData.totalItems || 0,
        totalPages: apiData.totalPages || 0,
        currentPage: apiData.currentPage || page,
        pageSize: apiData.pageSize || size,
        hasNext: apiData.hasNext || false,
        hasPrevious: apiData.hasPrevious || false
      };
    } catch (error) {
      console.error('[VisiblePatientsService] Error fetching visible patients:', error);
      return {
        patients: [],
        totalItems: 0,
        totalPages: 0,
        currentPage: page,
        pageSize: size,
        hasNext: false,
        hasPrevious: false
      };
    }
  }
}

export const visiblePatientsService = new VisiblePatientsService();
